import React, { useState } from "react";

import { faEllipsisH } from "@fortawesome/pro-regular-svg-icons";

import type { MeetingStatus } from "@performyard/models";
import { DropDownMenu, type DropDownMenuOption, Icon } from "@performyard/py-components";
import type { MeetingModelJson } from "@performyard/service-contracts";

import { StatusConfirmationModal } from "./StatusConfirmationModal";

import { useEditWorkspaceMeeting } from "../../../../api/hooks";
import {
	useMeetingCalendarIntegration,
	useVideoConferencingEligibility,
	useVideoConferencingFeatureEnabled,
} from "../hooks";

import { hasMeetingStarted } from "../../utils/meetingTiming";

import { AddVideoConferencingModal } from "./modals/AddVideoConferencingModal";

type ModalState =
	| { type: "none" }
	| { type: "remove-from-calendar"; isLoading: boolean }
	| { type: "remove-video-conferencing"; isLoading: boolean }
	| { type: "add-video-conferencing" }
	| { type: "status-change"; isLoading: boolean; status: MeetingStatus };

type MeetingRowOptionsDropdownProps = {
	meeting: MeetingModelJson;
	userId: string;
	onEditMeeting: (meeting: MeetingModelJson) => void;
	workspaceCreatorId: string;
};

const MeetingRowOptionsDropdown = ({
	meeting,
	userId,
	onEditMeeting,
	workspaceCreatorId,
}: MeetingRowOptionsDropdownProps): React.ReactElement | null => {
	const { mutate: editMeeting } = useEditWorkspaceMeeting();
	const [modalState, setModalState] = useState<ModalState>({ type: "none" });

	const { isCalendarIntegrationEnabled, hasCalendarEvent, isCalendarMismatch } =
		useMeetingCalendarIntegration(meeting);
	const videoConferencingFeatureEnabled = useVideoConferencingFeatureEnabled();
	const {
		eligible: isVideoConferencingEligible,
		adminEnabled: isVideoConferencingAdminEnabled,
		connectedProviders,
	} = useVideoConferencingEligibility();

	const isMeetingCreator = meeting.created?.userId === userId;
	const isWorkspaceCreator = workspaceCreatorId === userId;
	const canChangeStatus = isMeetingCreator || isWorkspaceCreator;
	const canEdit = meeting.status === "active" && (isMeetingCreator || isWorkspaceCreator);
	const hasVideoConferencing = !!meeting.videoConference;

	const getOptions = (): DropDownMenuOption[] => {
		const options: DropDownMenuOption[] = [];

		// "Edit" is the first option when user can edit
		if (canEdit) {
			options.push({
				key: "edit",
				label: "Edit",
				onClick: () => onEditMeeting(meeting),
			});
		}

		// Calendar integration options
		if (isCalendarIntegrationEnabled && canEdit) {
			if (hasCalendarEvent && !isCalendarMismatch) {
				options.push({
					key: "remove-from-calendar",
					label: "Remove from Calendar",
					onClick: () => setModalState({ type: "remove-from-calendar", isLoading: false }),
				});
			} else {
				options.push({
					key: "add-to-calendar",
					label: "Add to Calendar",
					onClick: () => {
						editMeeting({
							meetingId: meeting._id,
							data: { addToCalendar: true },
						});
					},
				});
			}
		}

		// Video conferencing options — mirrors the calendar pattern. Remove is shown
		// whenever the meeting already has VC (even if the user later disconnected),
		// so an orphan link is always clearable from the row menu — but the whole block
		// is suppressed when the VC feature flag is off or the company has disabled VC.
		if (
			canEdit &&
			videoConferencingFeatureEnabled &&
			isVideoConferencingAdminEnabled &&
			(isVideoConferencingEligible || hasVideoConferencing)
		) {
			if (hasVideoConferencing) {
				options.push({
					key: "remove-video-conferencing",
					label: "Remove Video Conferencing",
					onClick: () => setModalState({ type: "remove-video-conferencing", isLoading: false }),
				});
			} else if (connectedProviders.length > 0 && !hasMeetingStarted(meeting.startDateTime)) {
				// A single connected provider attaches directly; several open the provider picker
				// (PY-12435 — mirrors the details dropdown).
				options.push({
					key: "add-video-conferencing",
					label: "Add Video Conferencing",
					onClick: () => {
						if (connectedProviders.length === 1) {
							editMeeting({
								meetingId: meeting._id,
								data: { videoConferencingProvider: connectedProviders[0].provider },
							});
						} else {
							setModalState({ type: "add-video-conferencing" });
						}
					},
				});
			}
		}

		if (canChangeStatus) {
			// "Reopen Meeting" is an option when looking at non-active meetings
			if (meeting.status !== "active") {
				options.push({
					key: "active",
					label: "Reopen Meeting",
					onClick: () => {
						editMeeting({ meetingId: meeting._id, data: { status: "active" } });
					},
				});
			}

			// "Complete Meeting" is an option when looking at active, archived, or canceled meetings
			if (["active", "archived", "canceled"].includes(meeting.status)) {
				options.push({
					key: "completed",
					label: "Complete Meeting",
					onClick: () => setModalState({ type: "status-change", isLoading: false, status: "completed" }),
				});
			}

			// "Archive Meeting" is an option when looking at non-archived meetings
			if (meeting.status !== "archived") {
				options.push({
					key: "archived",
					label: "Archive Meeting",
					onClick: () => setModalState({ type: "status-change", isLoading: false, status: "archived" }),
				});
			}

			// "Cancel Meeting" is an option when looking at active meetings
			if (meeting.status === "active") {
				options.push({
					key: "canceled",
					label: "Cancel Meeting",
					onClick: () => setModalState({ type: "status-change", isLoading: false, status: "canceled" }),
				});
			}
		}

		return options;
	};

	const options = getOptions();

	// Don't render if no options available
	if (options.length === 0) {
		return <div className="h-8" />;
	}

	const handleConfirmStatusChange = (options: {
		removeFromCalendar?: boolean;
		removeVideoConferencing?: boolean;
	}): void => {
		if (modalState.type === "status-change" && modalState.status) {
			setModalState({ type: "status-change", isLoading: true, status: modalState.status });

			const updateData: {
				status: MeetingStatus;
				removeFromCalendar?: boolean;
				removeVideoConferencing?: boolean;
			} = {
				status: modalState.status,
			};

			if (modalState.status === "canceled") {
				if (options.removeFromCalendar) updateData.removeFromCalendar = true;
				if (options.removeVideoConferencing) updateData.removeVideoConferencing = true;
			}

			editMeeting(
				{ meetingId: meeting._id, data: updateData },
				{
					onSuccess: () => setModalState({ type: "none" }),
					onError: () =>
						setModalState({ type: "status-change", isLoading: false, status: modalState.status }),
				}
			);
		}
	};

	const handleRemoveFromCalendar = (): void => {
		setModalState({ type: "remove-from-calendar", isLoading: true });

		editMeeting(
			{
				meetingId: meeting._id,
				data: { removeFromCalendar: true },
			},
			{
				onSuccess: () => setModalState({ type: "none" }),
				onError: () => setModalState({ type: "remove-from-calendar", isLoading: false }),
			}
		);
	};

	const handleRemoveVideoConferencing = (): void => {
		setModalState({ type: "remove-video-conferencing", isLoading: true });

		editMeeting(
			{
				meetingId: meeting._id,
				data: { removeVideoConferencing: true },
			},
			{
				onSuccess: () => setModalState({ type: "none" }),
				onError: () => setModalState({ type: "remove-video-conferencing", isLoading: false }),
			}
		);
	};

	return (
		<>
			<div className="flex items-center justify-end">
				<DropDownMenu options={options} placement="bottom-end" className="group flex items-center">
					<Icon
						icon={faEllipsisH}
						size={24}
						color="neutral-7"
						dangerouslySetClassName="transition-colors group-hover:text-neutral-9"
					/>
				</DropDownMenu>
			</div>

			<StatusConfirmationModal
				isOpen={
					modalState.type === "status-change" ||
					modalState.type === "remove-from-calendar" ||
					modalState.type === "remove-video-conferencing"
				}
				isLoading={
					(modalState.type === "status-change" ||
						modalState.type === "remove-from-calendar" ||
						modalState.type === "remove-video-conferencing") &&
					modalState.isLoading
				}
				status={modalState.type === "status-change" ? modalState.status : null}
				action={
					modalState.type === "remove-from-calendar"
						? "remove-from-calendar"
						: modalState.type === "remove-video-conferencing"
							? "remove-video-conferencing"
							: "status-change"
				}
				onClose={() => setModalState({ type: "none" })}
				onConfirm={
					modalState.type === "remove-from-calendar"
						? handleRemoveFromCalendar
						: modalState.type === "remove-video-conferencing"
							? handleRemoveVideoConferencing
							: handleConfirmStatusChange
				}
				hasConnectedCalendarEvent={
					hasCalendarEvent &&
					!isCalendarMismatch &&
					(modalState.type === "remove-from-calendar" ||
						(modalState.type === "status-change" && modalState.status === "canceled"))
				}
				hasConnectedVideoConferencing={
					videoConferencingFeatureEnabled &&
					hasVideoConferencing &&
					modalState.type === "status-change" &&
					modalState.status === "canceled"
				}
				meetingName={meeting.name}
			/>

			<AddVideoConferencingModal
				isOpen={modalState.type === "add-video-conferencing"}
				onClose={() => setModalState({ type: "none" })}
				meetingId={meeting._id}
				connectedProviders={connectedProviders}
			/>
		</>
	);
};

export default MeetingRowOptionsDropdown;
